import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';

export default function AdminSetup() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [adminExists, setAdminExists] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    checkAdminExists();
  }, []);

  const checkAdminExists = async () => {
    try {
      const { data } = await supabase
        .from('users')
        .select('id')
        .eq('role', 'admin')
        .limit(1);
      if (data && data.length > 0) {
        setAdminExists(true);
      }
    } catch {
      // proceed
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = (pwd: string): string | null => {
    if (pwd.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(pwd)) return 'Password must contain at least one uppercase letter';
    if (!/[0-9]/.test(pwd)) return 'Password must contain at least one number';
    return null;
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    const pwdError = validatePassword(password);
    if (pwdError) {
      toast.error(pwdError);
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            role: 'admin',
          },
        },
      });

      if (error) throw error;

      toast.success('Admin account created successfully! You can now log in.');

      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create admin account');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  if (adminExists) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-golf-sand via-white to-golf-green/10 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <h1 className="text-2xl font-display font-bold text-golf-gray mb-4">Setup Already Complete</h1>
          <p className="text-gray-600 mb-6">An admin account already exists. Please log in.</p>
          <button
            onClick={() => navigate('/')}
            className="modern-button py-3 px-6 text-white font-medium"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-golf-sand via-white to-golf-green/10 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-golf-gray mb-2">
            Create Admin Account
          </h1>
          <p className="text-gray-600">
            Set up your administrator account to manage the tournament
          </p>
        </div>

        <form onSubmit={handleCreateAdmin} className="space-y-6">
          <div>
            <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              maxLength={100}
              placeholder="John Doe"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              maxLength={254}
              placeholder="admin@example.com"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-orange focus:border-transparent"
              required
              disabled={loading}
              minLength={8}
              placeholder="Min 8 characters, 1 uppercase, 1 number"
            />
            <p className="text-xs text-gray-500 mt-1">
              At least 8 characters, one uppercase letter, and one number
            </p>
          </div>

          <button
            type="submit"
            className="w-full modern-button py-3 text-white font-medium disabled:opacity-50"
            disabled={loading}
          >
            {loading ? 'Creating Account...' : 'Create Admin Account'}
          </button>

          <div className="text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-golf-orange hover:text-golf-green transition-colors"
              disabled={loading}
            >
              Already have an account? Go to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
